import { useRef, useState } from 'react';
import cn from 'classnames';
import { Spinner, TextButton, Typography, useOverlayContext } from 'seagull';
import { Plus } from 'seagull/icons';
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import styles from "./LogoPicker.module.scss";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export function readLogo(file, onChange, onNonPNG, onFileTooBig) {
  const fileSizeLimit = 5 * 1024 * 1024;
  if (!file) {
    return;
  }
  if (!file.name.toLowerCase().endsWith('.png')) {
    return onNonPNG();
  }
  if (file.size >= fileSizeLimit) {
    return onFileTooBig();
  }
  const reader = new FileReader();
  reader.onload = e => {
    const data = e.target ? {
      file,
      asDataURL: e.target.result
    } : undefined;
    onChange(data);
  };
  reader.readAsDataURL(file);
}

/** Renders a circle with a plus icon that chooses a .png file from the file-system with a clear button. */
export const LogoPicker = _ref => {
  let {
    image,
    loading,
    testId,
    isNewUI,
    onChange,
    noteBreakLine,
    inputRef
  } = _ref;
  const overlayContext = useOverlayContext();
  const logoInputRef = inputRef !== null && inputRef !== void 0 ? inputRef : useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const {
    BRANDING_IMPROVEMENTS = false
  } = useFeatureFlags();
  const readFile = file => readLogo(file, onChange, () => overlayContext.showErrorToast('Invalid file type: must be .png', 'file-type-error'), () => overlayContext.showErrorToast('Selected file is too large. Please select a smaller one.', 'file-size-error'));
  const onPickLogo = event => {
    if (!event.target.files || !event.target.files.length) {
      return;
    }
    readFile(event.target.files[0]);
  };
  const triggerInput = () => logoInputRef.current && logoInputRef.current.click();
  const onDrop = e => {
    e.preventDefault();
    if (e.dataTransfer.items.length && e.dataTransfer.items[0].kind === 'file') {
      const file = e.dataTransfer.items[0].getAsFile();
      readFile(file);
    }
    setIsDraggedOver(false);
  };
  const onDragOver = e => {
    e.preventDefault();
    setIsDraggedOver(true);
  };
  const onDragLeave = () => {
    setIsDraggedOver(false);
  };
  const renderNewUI = () => noteBreakLine ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TextButton, {
      className: styles.uploadButton,
      variant: BRANDING_IMPROVEMENTS ? 'paragraph3' : 'paragraph2',
      weight: BRANDING_IMPROVEMENTS ? 'normal' : 'light',
      underline: "none",
      onClick: triggerInput,
      children: image ? 'Change file' : 'Upload file'
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "paragraph3",
      skin: "secondary",
      children: " (PNG smaller than 5MB)"
    })]
  }) : /*#__PURE__*/_jsxs(Typography, {
    variant: "paragraph3",
    skin: "secondary",
    children: [/*#__PURE__*/_jsx(TextButton, {
      variant: BRANDING_IMPROVEMENTS ? 'paragraph3' : 'paragraph2',
      weight: BRANDING_IMPROVEMENTS ? 'normal' : 'light',
      underline: "none",
      onClick: triggerInput,
      children: image ? 'Change file' : 'Upload file'
    }), " (PNG smaller than 5MB)"]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: isNewUI ? cn(styles.uploadBox, {
        [styles.draggedOver]: isDraggedOver,
        [styles.hasLogo]: !!image
      }) : cn(styles.circle, {
        [styles.withBorder]: !image,
        [styles.draggedOver]: isDraggedOver
      }),
      onDrop: onDrop,
      onDragOver: onDragOver,
      onDragLeave: onDragLeave,
      children: [/*#__PURE__*/_jsx("input", {
        className: styles.input,
        ref: logoInputRef,
        type: "file",
        accept: ".png",
        "data-testid": testId && "".concat(testId, "-input"),
        onChange: onPickLogo
      }), /*#__PURE__*/_jsx("button", {
        className: styles.add,
        "data-testid": testId && "".concat(testId, "-add"),
        disabled: loading,
        onClick: triggerInput,
        children: image ? /*#__PURE__*/_jsx("img", {
          className: styles.image,
          alt: "Logo",
          src: image,
          "data-testid": testId && "".concat(testId, "-img")
        }) : isNewUI ? /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          children: "No logo"
        }) : /*#__PURE__*/_jsx(Plus, {})
      }), loading && /*#__PURE__*/_jsx("div", {
        className: styles.loading,
        children: /*#__PURE__*/_jsx(Spinner, {
          size: 40
        })
      })]
    }), isNewUI ? renderNewUI() : /*#__PURE__*/_jsxs(Typography, {
      variant: "paragraph2",
      className: styles.uploadLogoBox,
      children: ["Drag and drop your document here or", ' ', /*#__PURE__*/_jsx(TextButton, {
        variant: "paragraph2",
        onClick: triggerInput,
        children: "select file"
      })]
    })]
  });
};